import axios from 'axios';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TOKEN_KEY = 'paperbuddy_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

// Keeps user and token in sync.
export const startSession = (user, token) => {
  localStorage.setItem('user', JSON.stringify(user));
  if (token) localStorage.setItem(TOKEN_KEY, token);
};

export const clearSession = () => {
  localStorage.removeItem('user');
  localStorage.removeItem(TOKEN_KEY);
};

// Attach the token to every request.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Token ${token}`;
  return config;
});

// Expired or revoked token: drop the session and bounce to login.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && getToken()) {
      clearSession();
      if (window.location.pathname !== '/') {
        window.location.replace('/?expired=1');
      }
    }
    return Promise.reject(error);
  },
);

// API functions
export const register = async (username, email, password, name) => {
  const response = await api.post('/register/', { username, email, password, name });
  return response.data;
};

export const login = async (identifier, password) => {
  const response = await api.post('/login/', { username: identifier, password });
  return response.data;
};

export const logout = async () => {
  try {
    await api.post('/logout/');
  } finally {
    clearSession();
  }
};

export const verifyEmail = async (token) => {
  const response = await api.post('/verify-email/', { token });
  return response.data;
};

export const resendVerification = async (email) => {
  const response = await api.post('/resend-verification/', { email });
  return response.data;
};

export const forgotPassword = async (email) => {
  const response = await api.post('/forgot-password/', { email });
  return response.data;
};

export const resetPassword = async (token, password) => {
  const response = await api.post('/reset-password/', { token, password });
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/change-password/', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
};

export const listPapers = async () => {
  const response = await api.get('/papers/');
  return response.data;
};

export const getPaper = async (paperId) => {
  const response = await api.get(`/paper/${paperId}/`);
  return response.data;
};

export const getPaperLayout = async (paperId) => {
  const response = await api.get(`/paper/${paperId}/layout/`);
  return response.data;
};

export const getReadingSession = async (userId, paperId) => {
  const response = await api.get('/reading-session/', {
    params: { user_id: userId, paper_id: paperId }
  });
  return response.data;
};

export const updateLastPage = async (userId, paperId, lastPage) => {
  const response = await api.post('/update-last-page/', {
    user_id: userId,
    paper_id: paperId,
    last_page: lastPage,
  });
  return response.data;
};


export const sendMessage = async (userId, paperId, sessionNumber, message, currentPage, paragraphText, mode = 'student', testQuestions = null, silent = false, chatIndex = null, cyclePlan = null, guidedTargetMode = null, questionNumber = null, annotationId = null) => {
  const response = await api.post('/send-message/', {
    user_id: userId,
    paper_id: paperId,
    session_number: sessionNumber,
    message: message,
    mode,
    ...(silent && { silent: true }),
    ...(currentPage != null && { current_page: currentPage }),
    ...(paragraphText && { paragraph_text: paragraphText }),
    ...(testQuestions && { test_questions: testQuestions }),
    ...(chatIndex != null && { chat_index: chatIndex }),
    ...(cyclePlan && { cycle_plan: cyclePlan }),
    ...(guidedTargetMode && { guided_target_mode: guidedTargetMode }),
    ...(questionNumber != null && { question_number: questionNumber }),
    ...(annotationId != null && { annotation_id: annotationId }),
  });
  return response.data;
};

// Incremental cycle-plan authoring (instructor). step='overview' returns a
// lightweight skeleton {response, overview}; step='detail' returns a single
// detailed cycle {response, cycle, actions}. Uses the instructor session (0);
// chatIndex selects/creates the thread for the current cycle.
export const authorCycle = async ({ userId, paperId, step, message, overview = null, cycleNumber = null, chatIndex = null, silent = false }) => {
  const response = await api.post('/send-message/', {
    user_id: userId,
    paper_id: paperId,
    session_number: 0,
    message,
    mode: 'cycle_author',
    step,
    ...(silent && { silent: true }),
    ...(overview && { overview }),
    ...(cycleNumber != null && { cycle_number: cycleNumber }),
    ...(chatIndex != null && { chat_index: chatIndex }),
  });
  return response.data;
};

// Resolve instructor-pinned highlights ([{page, text, exact?}]) to drawable rect
// groups, so the student client can draw them deterministically at cycle/test start.
export const resolveHighlights = async (paperId, highlights) => {
  const response = await api.post('/resolve-highlights/', {
    paper_id: paperId,
    highlights,
  });
  return response.data;
};


export const unlockSession = async (userId, paperId, sessionNumber) => {
  const response = await api.post('/unlock-session/', {
    user_id: userId,
    paper_id: paperId,
    session_number: sessionNumber
  });
  return response.data;
};

// Update current session
export const updateCurrentSession = async (userId, paperId, sessionNumber) => {
  const response = await api.post('/update-current-session/', {
    user_id: userId,
    paper_id: paperId,
    session_number: sessionNumber
  });
  return response.data;
};


export const getConversationHistory = async (userId, paperId, sessionNumber, chatIndex = null) => {
  const response = await api.get('/conversation-history/', {
    params: {
      user_id: userId,
      paper_id: paperId,
      session_number: sessionNumber,
      ...(chatIndex != null && { chat_index: chatIndex }),
    },
  });
  return response.data;
};

export const listChats = async (userId, paperId, sessionNumber) => {
  const response = await api.get('/chat-list/', {
    params: { user_id: userId, paper_id: paperId, session_number: sessionNumber },
  });
  return response.data;
};

export const clearHistory = async (userId) => {
  const response = await api.delete('/clear-history/', { params: { user_id: userId } });
  return response.data;
};

export const deleteChat = async (userId, paperId, sessionNumber, chatIndex) => {
  const response = await api.delete('/chat-delete/', {
    params: { user_id: userId, paper_id: paperId, session_number: sessionNumber, chat_index: chatIndex },
  });
  return response.data;
};

export const newChat = async (userId, paperId, sessionNumber, planId = null) => {
  const response = await api.post('/new-chat/', {
    user_id: userId,
    paper_id: paperId,
    session_number: sessionNumber,
    ...(planId != null && { plan_id: planId }),
  });
  return response.data;
};

// Generate (or regenerate) a short title for one conversation thread (sidebar).

// List a test run's per-question threads (for the question navigator).
export const getTestRunThreads = async (userId, paperId, sessionNumber, rootChatIndex) => {
  const response = await api.get('/test-run-threads/', {
    params: { user_id: userId, paper_id: paperId, session_number: sessionNumber, root_chat_index: rootChatIndex },
  });
  return response.data;
};

// Refresh a thread's cross-thread-memory summary (call when switching away from a thread).
export const summarizeThread = async (userId, paperId, sessionNumber, chatIndex) => {
  const response = await api.post('/summarize-thread/', {
    user_id: userId, paper_id: paperId,
    session_number: sessionNumber, chat_index: chatIndex,
  });
  return response.data;
};

// End (abandon) an in-progress test so the test-integrity lock releases.
export const exitTest = async (userId, paperId, planId = null) => {
  const response = await api.post('/exit-test/', {
    user_id: userId, paper_id: paperId,
    ...(planId != null && { plan_id: planId }),
  });
  return response.data;
};

// Session progress — durable "pick up where you left off" pointer.
// getSessionProgress returns one row per mode the user has touched for this paper.
export const getSessionProgress = async (userId, paperId) => {
  const response = await api.get('/session-progress/', {
    params: { user_id: userId, paper_id: paperId },
  });
  return response.data;
};

// saveSessionProgress upserts the resume pointer for one mode (fire-and-forget).
export const saveSessionProgress = async ({
  userId, paperId, mode, sessionNumber = 1, chatIndex = null,
  currentPosition = 0, status = 'in_progress', snapshot = {},
}) => {
  const response = await api.post('/session-progress/', {
    user_id: userId,
    paper_id: paperId,
    mode,
    session_number: sessionNumber,
    chat_index: chatIndex,
    current_position: currentPosition,
    status,
    snapshot,
  });
  return response.data;
};

// Notes
export const listNotes = async (userId, paperId) => {
  const response = await api.get('/notes/', {
    params: { user_id: userId, paper_id: paperId },
  });
  return response.data;
};

export const createNote = async (userId, paperId, pageNumber, body, paragraphExcerpt = '') => {
  const response = await api.post('/notes/', {
    user_id: userId,
    paper_id: paperId,
    page_number: pageNumber,
    paragraph_excerpt: paragraphExcerpt,
    body,
  });
  return response.data;
};

export const deleteNote = async (noteId) => {
  const response = await api.delete(`/notes/${noteId}/`);
  return response.data;
};

export const updateNote = async (noteId, body) => {
  const response = await api.patch(`/notes/${noteId}/`, { body });
  return response.data;
};

// Instructor custom instructions
export const getPaperInstructions = async (paperId) => {
  const response = await api.get(`/paper/${paperId}/instructions/`);
  return response.data;
};

export const savePaperInstructions = async (paperId, customInstructions) => {
  const response = await api.post(`/paper/${paperId}/instructions/`, {
    custom_instructions: customInstructions,
  });
  return response.data;
};

export const summarizePaperInstructions = async (paperId, customInstructions) => {
  const response = await api.post(`/paper/${paperId}/summarize-instructions/`, {
    custom_instructions: customInstructions,
  });
  return response.data;
};

// Plan history
export const getPlanHistory = async (paperId, userId) => {
  const response = await api.get('/plan-history/', { params: { paper_id: paperId, user_id: userId } });
  return response.data;
};

export const setActivePlan = async (planId, userId, paperId, dueDate = null) => {
  const response = await api.post('/set-active-plan/', {
    plan_id: planId, user_id: userId, paper_id: paperId,
    ...(dueDate && { due_date: dueDate }),
  });
  return response.data;
};

export const getModeSettings = async (paperId) => {
  const response = await api.get(`/paper/${paperId}/mode-settings/`);
  return response.data;
};

export const saveModeSettings = async (paperId, settings) => {
  const response = await api.post(`/paper/${paperId}/mode-settings/`, settings);
  return response.data;
};

export const deactivatePlans = async (userId, paperId, planType) => {
  const response = await api.post('/deactivate-plans/', { user_id: userId, paper_id: paperId, plan_type: planType });
  return response.data;
};

export const deletePlan = async (planId, userId) => {
  const response = await api.delete(`/plan/${planId}/`, { params: { user_id: userId } });
  return response.data;
};

export const duplicatePlan = async (planId, userId) => {
  const response = await api.post(`/plan/${planId}/duplicate/`, { user_id: userId });
  return response.data;
};

export const renamePlan = async (planId, userId, name) => {
  const response = await api.patch(`/plan/${planId}/rename/`, { user_id: userId, name });
  return response.data;
};

// Glossary
export const defineTerm = async (paperId, term) => {
  const response = await api.post('/define/', { paper_id: paperId, term });
  return response.data;
};

// Algorithm / equation walkthrough
export const getWalkthrough = async (paperId, selection) => {
  const response = await api.post('/walkthrough/', { paper_id: paperId, selection });
  return response.data;
};

// Test sessions
export const getActiveTest = async (paperId, userId = null) => {
  const params = { paper_id: paperId };
  if (userId != null) params.user_id = userId;
  const response = await api.get('/active-test/', { params });
  return response.data;
};

export const saveTestResults = async (userId, paperId, planId, grades, averageGrade, aiFlags, hintsUsed, chatIndex = null) => {
  const response = await api.post('/save-test-results/', {
    user_id: userId,
    paper_id: paperId,
    plan_id: planId,
    grades,
    average_grade: averageGrade,
    ai_flags: aiFlags,
    total_hints_used: hintsUsed,
    ...(chatIndex != null && { chat_index: chatIndex }),
  });
  return response.data;
};

export const getTestSessionConversation = async (sessionId) => {
  const response = await api.get('/test-session-conversation/', { params: { session_id: sessionId } });
  return response.data;
};

export const getCycleSessionConversation = async (sessionId) => {
  const response = await api.get('/cycle-session-conversation/', { params: { session_id: sessionId } });
  return response.data;
};

export const getTestResults = async (paperId) => {
  const response = await api.get('/test-results/', { params: { paper_id: paperId } });
  return response.data;
};

// Instructor: permanently delete one student attempt (session + transcript).
export const deleteTestAttempt = async (instructorId, sessionId) => {
  const response = await api.delete(`/test-attempt/${sessionId}/`, { params: { user_id: instructorId } });
  return response.data;
};

export const deleteCycleAttempt = async (instructorId, sessionId) => {
  const response = await api.delete(`/cycle-attempt/${sessionId}/`, { params: { user_id: instructorId } });
  return response.data;
};

export const getActiveCycle = async (paperId) => {
  const response = await api.get('/active-cycle/', { params: { paper_id: paperId } });
  return response.data;
};

// Instructor lesson plan
export const savePlan = async (userId, paperId, planJson, dueDate = null, name = '', planId = null) => {
  const response = await api.post('/save-plan/', {
    user_id: userId, paper_id: paperId, plan_json: planJson,
    ...(dueDate && { due_date: dueDate }),
    ...(name && { name }),
    ...(planId && { plan_id: planId }),
  });
  return response.data;
};

export const exportTestResultsCsv = (paperId) => {
  window.open(`${API_BASE_URL}/export-test-results-csv/?paper_id=${paperId}`, '_blank');
};

export const exportCycleResultsCsv = (paperId) => {
  window.open(`${API_BASE_URL}/export-cycle-results-csv/?paper_id=${paperId}`, '_blank');
};

// Session recap + flashcards
export const getSessionRecap = async (userId, paperId, sessionNumber, chatIndex = null) => {
  const body = {
    user_id: userId,
    paper_id: paperId,
    session_number: sessionNumber,
  };
  if (chatIndex != null) body.chat_index = chatIndex;
  // Recap runs an LLM call and can take 30–90s
  const response = await api.post('/recap/', body, { timeout: 120000 });
  return response.data;
};

// Instructor analytics
export const getInstructorAnalytics = async (paperId, planId = null) => {
  const params = { paper_id: paperId };
  if (planId) params.plan_id = planId;
  const response = await api.get('/instructor-analytics/', { params });
  return response.data;
};

export const getInstructorCycleAnalytics = async (paperId, planId = null) => {
  const params = { paper_id: paperId };
  if (planId) params.plan_id = planId;
  const response = await api.get('/instructor-cycle-analytics/', { params });
  return response.data;
};

// Cycle progress (called from student cycle mode as cycles complete)
export const saveCycleProgress = async ({
  userId, paperId, planId = null, chatIndex = null,
  event, totalCycles = null, cycleNumber = null, messageCount = null,
}) => {
  const response = await api.post('/save-cycle-progress/', {
    user_id: userId,
    paper_id: paperId,
    plan_id: planId,
    chat_index: chatIndex,
    event,
    ...(totalCycles != null && { total_cycles: totalCycles }),
    ...(cycleNumber != null && { cycle_number: cycleNumber }),
    ...(messageCount != null && { message_count: messageCount }),
  });
  return response.data;
};

export const generateInsights = async (paperId, analyticsData) => {
  const response = await api.post('/generate-insights/', { paper_id: paperId, analytics_data: analyticsData });
  return response.data;
};

// Analytics Assistant
export const getAnalyticsStats = async (paperId, startDate = null, endDate = null) => {
  const params = { paper_id: paperId };
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get('/analytics-assistant/stats/', { params });
  return response.data;
};

export const sendAnalyticsMessage = async (paperId, message, history = [], startDate = null, endDate = null) => {
  const response = await api.post('/analytics-assistant/chat/', {
    paper_id: paperId,
    message,
    history,
    ...(startDate ? { start_date: startDate } : {}),
    ...(endDate ? { end_date: endDate } : {}),
  }, { timeout: 60000 });
  return response.data;
};

export const generateTopicPriorities = async (paperId, questionSummary) => {
  const response = await api.post('/generate-topic-priorities/', { paper_id: paperId, question_summary: questionSummary });
  return response.data;
};

export const suggestSections = async (paperId, force = false) => {
  const response = await api.get(`/paper/${paperId}/suggest-sections/`, force ? { params: { force: '1' } } : {});
  return response.data;
};

export const modifySections = async (paperId, sections, request, goals = null) => {
  const response = await api.post(`/paper/${paperId}/modify-sections/`, { sections, request, ...(goals && { goals }) });
  return response.data;
};

export const suggestSectionSettings = async (paperId, goals = null) => {
  const response = await api.get(`/paper/${paperId}/suggest-section-settings/`, goals ? { params: { goals } } : {});
  return response.data;
};

export const suggestPresets = async (paperId, goals, sections, targetMode, tocSections = []) => {
  const response = await api.post(`/paper/${paperId}/suggest-presets/`, {
    goals, sections, target_mode: targetMode,
    ...(tocSections?.length ? { toc_sections: tocSections } : {}),
  });
  return response.data;
};

export const suggestTopics = async (paperId, sections) => {
  const response = await api.post(`/paper/${paperId}/suggest-topics/`, { sections });
  return response.data;
};

export const suggestTopicWeights = async (paperId, topics) => {
  const response = await api.post(`/paper/${paperId}/suggest-topic-weights/`, { topics });
  return response.data;
};

export const retakeTest = async (paperId, planId, userId) => {
  const response = await api.post(`/paper/${paperId}/retake-test/`, { plan_id: planId, user_id: userId });
  return response.data;
};

export const generateCycleTopicPriorities = async (paperId, cycleBreakdown) => {
  const response = await api.post('/generate-cycle-topic-priorities/', { paper_id: paperId, cycle_breakdown: cycleBreakdown });
  return response.data;
};

// ── Q&A analytics + instructor grading / interjection / hallucination flags ──

export const getInstructorQAAnalytics = async (paperId) => {
  const response = await api.get('/instructor-qa-analytics/', { params: { paper_id: paperId } });
  return response.data;
};

// Auto-score a Q&A conversation for genuine engagement (cached after first run).
// Pass force=true to recompute. LLM call — can take 10–30s.
export const autoScoreConversation = async (userId, conversationId, force = false) => {
  const response = await api.post('/auto-score-conversation/', {
    user_id: userId, conversation_id: conversationId, force,
  }, { timeout: 60000 });
  return response.data;
};

// Record an instructor's adjustment to a Q&A conversation's engagement score + note.
export const scoreConversation = async (userId, conversationId, score, note = '') => {
  const response = await api.post('/score-conversation/', {
    user_id: userId, conversation_id: conversationId, score, note,
  });
  return response.data;
};

// Instructor override of a single test question's grade. Recomputes the session average.
export const patchTestQuestionGrade = async (userId, resultId, { numericalGrade, categoricalGrade, note } = {}) => {
  const response = await api.patch('/test-question-grade/', {
    user_id: userId,
    result_id: resultId,
    ...(numericalGrade != null && { numerical_grade: numericalGrade }),
    ...(categoricalGrade != null && { categorical_grade: categoricalGrade }),
    ...(note != null && { note }),
  });
  return response.data;
};

// Private instructor note on a conversation (or a specific message). Never shown to students.
export const listAnnotations = async (conversationId) => {
  const response = await api.get('/annotate-conversation/', { params: { conversation_id: conversationId } });
  return response.data;
};

export const annotateConversation = async (userId, conversationId, body, messageId = null) => {
  const response = await api.post('/annotate-conversation/', {
    user_id: userId, conversation_id: conversationId, body,
    ...(messageId != null && { message_id: messageId }),
  });
  return response.data;
};

// Post an instructor message into a student's thread (the student sees it).
export const postInstructorMessage = async (userId, conversationId, message) => {
  const response = await api.post('/instructor-message/', {
    user_id: userId, conversation_id: conversationId, message,
  });
  return response.data;
};

// Hallucination flags + annotations overlaid on a transcript.
export const getConversationFlags = async (conversationId) => {
  const response = await api.get('/conversation-flags/', { params: { conversation_id: conversationId } });
  return response.data;
};

// On-demand LLM scan of one conversation's AI messages. Scan can take 20–60s.
export const scanHallucinations = async (userId, conversationId) => {
  const response = await api.post('/scan-hallucinations/', {
    user_id: userId, conversation_id: conversationId,
  }, { timeout: 120000 });
  return response.data;
};

// Manually flag an AI message as a hallucination.
export const createHallucinationFlag = async (userId, messageId, { severity, claim, rationale, correction } = {}) => {
  const response = await api.post('/hallucination-flag/', {
    user_id: userId, message_id: messageId,
    ...(severity && { severity }),
    ...(claim && { claim }),
    ...(rationale && { rationale }),
    ...(correction && { correction }),
  });
  return response.data;
};

// Confirm / dismiss / correct an existing flag.
export const updateHallucinationFlag = async (userId, flagId, { status: flagStatus, correction } = {}) => {
  const response = await api.patch(`/hallucination-flag/${flagId}/`, {
    user_id: userId,
    ...(flagStatus && { status: flagStatus }),
    ...(correction != null && { correction }),
  });
  return response.data;
};

// Override (false positive) or restore a cycle-mode AI-authorship flag.
// flagStatus: 'overridden' to dismiss as a false positive, 'active' to restore.
export const overrideCycleAiFlag = async (userId, flagId, flagStatus) => {
  const response = await api.patch(`/cycle-ai-flag/${flagId}/`, {
    user_id: userId,
    status: flagStatus,
  });
  return response.data;
};

// Test-mode counterpart of overrideCycleAiFlag.
export const overrideTestAiFlag = async (userId, flagId, flagStatus) => {
  const response = await api.patch(`/test-ai-flag/${flagId}/`, {
    user_id: userId,
    status: flagStatus,
  });
  return response.data;
};

// Q&A-mode counterpart of overrideCycleAiFlag / overrideTestAiFlag.
export const overrideQaAiFlag = async (userId, flagId, flagStatus) => {
  const response = await api.patch(`/qa-ai-flag/${flagId}/`, {
    user_id: userId,
    status: flagStatus,
  });
  return response.data;
};

// Glossary
export const getGlossary = async (paperId, userId) => {
  const response = await api.get('/glossary/', { params: { paper_id: paperId, user_id: userId } });
  return response.data;
};

export const addGlossaryTerm = async (userId, paperId, term, contextSnippet = '', pageNumber = null, definition = '') => {
  const response = await api.post('/glossary/add/', {
    user_id: userId, paper_id: paperId, term,
    context_snippet: contextSnippet, page_number: pageNumber, definition,
  });
  return response.data;
};

export const deleteGlossaryTerm = async (termId, userId) => {
  const response = await api.delete(`/glossary/${termId}/`, { params: { user_id: userId } });
  return response.data;
};

export const getAnnotations = async (paperId) => {
  const response = await api.get('/annotations/', { params: { paper_id: paperId } });
  return response.data;
};

export const createAnnotation = async (paperId, page, anchorText, body, kind = 'question', rects = []) => {
  const response = await api.post('/annotations/', {
    paper_id: paperId, page, anchor_text: anchorText, body, kind, rects,
  });
  return response.data;
};

export const updateAnnotation = async (annotationId, { body, kind } = {}) => {
  const payload = {};
  if (body !== undefined) payload.body = body;
  if (kind !== undefined) payload.kind = kind;
  const response = await api.patch(`/annotations/${annotationId}/`, payload);
  return response.data;
};

export const deleteAnnotation = async (annotationId) => {
  const response = await api.delete(`/annotations/${annotationId}/`);
  return response.data;
};

export const startAnnotationChat = async (annotationId) => {
  const response = await api.post('/annotation-chat/', { annotation_id: annotationId });
  return response.data;
};

export default api;